import React, { useEffect, useState } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { keyframes, styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import DownloadIcon from "@mui/icons-material/Download";

// ── keyframes ──
const backdropIn = keyframes`from{opacity:0}to{opacity:1}`;
const modalIn = keyframes`from{opacity:0;transform:scale(.88) translateY(16px)}to{opacity:1;transform:scale(1) translateY(0)}`;
const shimmer = keyframes`0%{background-position:-400px 0}100%{background-position:400px 0}`;

const Backdrop = styled(Box)({
  position: "fixed",
  inset: 0,
  zIndex: 1400,
  background: "rgba(7,10,18,.86)",
  backdropFilter: "blur(14px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  animation: `${backdropIn} .22s ease both`,
  padding: "20px",
});

const ModalWrap = styled(Box)({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  animation: `${modalIn} .28s cubic-bezier(.22,1,.36,1) both`,
  maxWidth: 480,
  width: "100%",
});

const ImageWrap = styled(Box)(({ zoom }) => ({
  width: "100%",
  borderRadius: 20,
  overflow: "hidden",
  background: "#0d111c",
  border: "1px solid rgba(255,255,255,.08)",
  boxShadow:
    "0 40px 80px -20px rgba(0,0,0,.6), 0 0 0 1px rgba(255,255,255,.04)",
  cursor: zoom > 1 ? "zoom-out" : "zoom-in",
  transition: "box-shadow .2s ease",
  "&:hover": {
    boxShadow:
      "0 48px 96px -20px rgba(0,0,0,.7), 0 0 0 1px rgba(255,255,255,.07)",
  },
}));

const ControlBar = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginTop: 16,
  background: "rgba(255,255,255,.05)",
  border: "1px solid rgba(255,255,255,.08)",
  borderRadius: 100,
  padding: "5px 10px",
  backdropFilter: "blur(8px)",
});

const CtrlBtn = styled(IconButton)({
  width: 32,
  height: 32,
  borderRadius: "50%",
  color: "rgba(255,255,255,.65)",
  transition: "all .18s ease",
  "&:hover": {
    background: "rgba(255,255,255,.1)",
    color: "#fff",
    transform: "scale(1.08)",
  },
});

const CloseBtn = styled(IconButton)({
  position: "absolute",
  top: -14,
  right: -14,
  width: 36,
  height: 36,
  background: "rgba(255,255,255,.08)",
  border: "1px solid rgba(255,255,255,.1)",
  color: "rgba(255,255,255,.8)",
  backdropFilter: "blur(8px)",
  zIndex: 10,
  transition: "all .18s ease",
  "&:hover": {
    background: "rgba(244,63,94,.2)",
    borderColor: "rgba(244,63,94,.4)",
    color: "#f87171",
    transform: "scale(1.08) rotate(90deg)",
  },
});

const Divider = styled(Box)({
  width: 1,
  height: 18,
  background: "rgba(255,255,255,.12)",
  margin: "0 4px",
});

/**
 * ImagePreviewModal — Reusable full-screen image preview.
 *
 * Props:
 *   open        {boolean}  — controls visibility
 *   onClose     {function} — called when user closes
 *   src         {string}   — image URL
 *   name        {string?}  — optional label shown below image
 *   subtitle    {string?}  — optional smaller subtitle
 *   fallback    {string?}  — fallback text/initials if src is missing (renders avatar-style)
 *   avatarColor {string?}  — gradient string for fallback avatar bg
 */

const ImagePreviewModal = ({
  open,
  onClose,
  src,
  name,
  subtitle,
  fallback,
  avatarColor = "linear-gradient(135deg,#0288D1,#0ea5e9)",
}) => {
  const [zoom, setZoom] = useState(1);
  const [imgLoaded, setImgLoaded] = useState(false);

  // Reset state when opened
  useEffect(() => {
    if (open) {
      setZoom(1);
      setImgLoaded(false);
    }
  }, [open, src]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  const handleDownload = async () => {
    if (!src) return;

    try {
      // 1. Fetch the image data
      const response = await fetch(src);

      // 2. Convert to a blob (binary data)
      const blob = await response.blob();

      // 3. Create a local "Object URL" that points to that blob
      const url = window.URL.createObjectURL(blob);

      // 4. Create the invisible link and click it
      const a = document.createElement("a");
      a.href = url;
      a.download = name || "downloaded-image";
      document.body.appendChild(a); // Append for better browser compatibility
      a.click();

      // 5. Clean up: remove the link and revoke the URL to save memory
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
      // Fallback: if fetch fails (due to CORS), open in new tab as last resort
      window.open(src, "_blank");
    }
  };

  if (!open) return null;

  return (
    <Backdrop onClick={onClose}>
      <ModalWrap onClick={(e) => e.stopPropagation()}>
        <CloseBtn onClick={onClose} size="small">
          <CloseIcon sx={{ fontSize: 16 }} />
        </CloseBtn>

        <ImageWrap
          zoom={zoom}
          onClick={() => setZoom((z) => (z > 1 ? 1 : 1.8))}
        >
          {src ? (
            <>
              {/* Shimmer placeholder while loading */}
              {!imgLoaded && (
                <Box
                  sx={{
                    width: "100%",
                    height: 320,
                    background:
                      "linear-gradient(90deg,#0d111c 25%,#151b2e 50%,#0d111c 75%)",
                    backgroundSize: "400px 100%",
                    animation: `${shimmer} 1.4s ease-in-out infinite`,
                  }}
                />
              )}
              <Box
                component="img"
                src={src}
                alt={name || "Preview"}
                onLoad={() => setImgLoaded(true)}
                sx={{
                  width: "100%",
                  display: imgLoaded ? "block" : "none",
                  transform: `scale(${zoom})`,
                  transformOrigin: "center center",
                  transition: "transform .28s cubic-bezier(.22,1,.36,1)",
                  userSelect: "none",
                  WebkitUserDrag: "none",
                }}
              />
            </>
          ) : (
            /* Fallback avatar when no src */
            <Box
              sx={{
                width: "100%",
                height: 320,
                background: avatarColor,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "'Sora',sans-serif",
                  fontWeight: 800,
                  fontSize: "5rem",
                  color: "rgba(255,255,255,.9)",
                  letterSpacing: ".04em",
                  userSelect: "none",
                }}
              >
                {fallback || "?"}
              </Typography>
            </Box>
          )}
        </ImageWrap>

        {/* Name / subtitle */}
        {(name || subtitle) && (
          <Box sx={{ textAlign: "center", mt: 2, px: 2 }}>
            {name && (
              <Typography
                sx={{
                  fontFamily: "'Sora',sans-serif",
                  fontWeight: 700,
                  fontSize: "1rem",
                  color: "#fff",
                  textTransform: "capitalize",
                  lineHeight: 1.3,
                }}
              >
                {name}
              </Typography>
            )}
            {subtitle && (
              <Typography
                sx={{
                  fontFamily: "'Sora',sans-serif",
                  fontSize: ".76rem",
                  color: "rgba(255,255,255,.4)",
                  mt: 0.3,
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        )}

        {/* Controls */}
        {src && (
          <ControlBar>
            <CtrlBtn
              onClick={() => setZoom((z) => Math.max(1, z - 0.4))}
              size="small"
            >
              <ZoomOutIcon sx={{ fontSize: 16 }} />
            </CtrlBtn>
            <Typography
              sx={{
                fontFamily: "'Sora',sans-serif",
                fontSize: ".72rem",
                fontWeight: 700,
                color: "rgba(255,255,255,.5)",
                minWidth: 36,
                textAlign: "center",
                userSelect: "none",
              }}
            >
              {Math.round(zoom * 100)}%
            </Typography>
            <CtrlBtn
              onClick={() => setZoom((z) => Math.min(3, z + 0.4))}
              size="small"
            >
              <ZoomInIcon sx={{ fontSize: 16 }} />
            </CtrlBtn>
            <Divider />
            <CtrlBtn onClick={handleDownload} size="small">
              <DownloadIcon sx={{ fontSize: 16 }} />
            </CtrlBtn>
          </ControlBar>
        )}

        {/* Hint */}
        <Typography
          sx={{
            fontFamily: "'Sora',sans-serif",
            fontSize: ".68rem",
            color: "rgba(255,255,255,.2)",
            mt: 1.5,
            userSelect: "none",
          }}
        >
          Click image to zoom · ESC to close
        </Typography>
      </ModalWrap>
    </Backdrop>
  );
};

export default ImagePreviewModal;
